/**
 * FEATURE CATALOG — the typed configuration framework layered on top of the
 * persona/module identity already defined in `roles.ts` (PR #96).
 *
 * `roles.ts` owns the persona codes and the PERSONA_MODULES seed (persona →
 * module-id[]). This file does NOT re-declare that seed. It adds the missing
 * typed layer: a `ModuleId` union, runtime guards, and a `FEATURE_CATALOG` that
 * maps each module id to the SURFACE it gates (a MemberProfile tab, an app
 * route, or a not-yet-built feature). The access layer (`src/access/*`) reads
 * from here. Keep this the single source for module → surface mapping.
 *
 * SINGLE SOURCE OF TRUTH: the set of module ids is anchored to `roles.ts`.
 * `roles.ts` types `PERSONA_MODULES` values as `ModuleId[]` (imported from here),
 * so a module id in the persona seed that is not a member of the `ModuleId` union
 * is a COMPILE-TIME error in `roles.ts`. In addition, the DEV-only runtime check
 * at the bottom of this file logs a `console.warn` if a seed id lacks a
 * FEATURE_CATALOG entry — a redundant belt-and-braces guard, no-op in production.
 */

import { PERSONA_MODULES } from "./roles";

// ---------------------------------------------------------------------------
// ModuleId — the module ids of the AstraConnect persona vocabulary.
// ---------------------------------------------------------------------------
/**
 * The module ids that personas can be granted. These are the same strings used
 * as values in `roles.ts` PERSONA_MODULES (the compile-time check below proves
 * the catalog covers every id that seed references).
 */
export type ModuleId =
  | "Home"
  | "HomeDemographics"
  | "HomeNextBestAction"
  | "HomeClinicalSummary"
  | "HomeInpatientEd"
  | "HomeAnnualWellness"
  | "HomeQualityGaps"
  | "HomeHcc"
  | "HomeCases"
  | "HomeCareTeam"
  | "Activity"
  | "Medications"
  | "AmbientAI"
  | "WriteBack"
  | "Auths"
  | "MemberSearch"
  | "Eligibility"
  | "Claims"
  | "CareTeam"
  | "Outreach"
  | "NetworkSearch"
  | "Documents"
  | "Notes"
  | "PracticeSchedule"
  | "AdtNotifications"
  | "PriorityWorklist"
  // AC-ADM-3 (0027): the last three tabs that bypassed the persona gate in
  // member-360 and so were never grantable or orderable from this console.
  | "Diagnoses"
  | "Labs"
  | "Scribe";

// The Documents tab name. MemberProfilePage does not render a Documents tab yet
// (it's a future @astrana/ui surface), so its tabName matches the id verbatim.
// When the tab lands, point this at the shared DOCUMENTS_TAB constant the page
// uses so the catalog and the page can never drift apart.
const DOCUMENTS_TAB = "Documents";

// ---------------------------------------------------------------------------
// FEATURE_CATALOG — module id → the surface it gates.
// ---------------------------------------------------------------------------
/**
 * What surface each module id controls:
 *   - `tab`     → a MemberProfile tab, matched by `tabName`.
 *   - `route`   → a full-page app route (e.g. Member Search at /members).
 *   - `feature` → a feature with no current FE surface, registered for the
 *                 future so the access layer recognises the id without crashing.
 */
export interface FeatureCatalogEntry {
  kind: "tab" | "route" | "feature";
  label: string;
  /** Set for `kind: 'tab'` — the MemberProfile ModuleGroup.tabName it controls. */
  tabName?: string;
  /** Set for `kind: 'route'` — the app route it controls. */
  route?: string;
  /**
   * The set of action keys this module supports — the REGISTRY of what CAN be
   * mapped for a persona. The backend's per-persona `actions[]` in
   * `connectCurrentUser.currentPersona.modules[].actions` decides which are
   * ENABLED at runtime; this list is the vocabulary the backend may reference.
   * Keep it minimal: only add keys the backend actually emits or plans to emit.
   */
  actions?: readonly string[];
}

export const FEATURE_CATALOG: Record<ModuleId, FeatureCatalogEntry> = {
  Home: { kind: "tab", label: "Home", tabName: "Home" },
  // A sub-card WITHIN the Home tab (grouped under the "Home" parent module by
  // MODULE_GROUPS in PersonasPage.tsx), not a tab of its own — hence `feature`
  // (no dedicated tabName/route). Registered here purely so the admin console
  // labels it "Demographics" (instead of the raw id) and it's addable via a
  // catalog-aware picker. Its collapsed/expanded default is a separate concern
  // encoded in the module's `actions[]` (a `"collapsedByDefault"` sentinel),
  // not modeled in this catalog.
  HomeDemographics: { kind: "feature", label: "Demographics" },
  // CM-8319 — the engine-selected Next Best Action. Like HomeDemographics this is
  // a sub-card WITHIN the Home tab, so `feature` rather than `tab`: PersonasPage's
  // MODULE_GROUPS nests it under Home via its `id.startsWith("Home")` heuristic,
  // and it inherits the ACT-1309 drag reorder for free.
  //
  // No `actions`. Granting the card is the whole decision — the two affordances it
  // renders (go to the action, mark it done) are not separately gateable, and the
  // agent-run mutations are inert stubs server-side, so there is nothing a
  // create/edit action could unlock.
  //
  // Label MUST match member-360-standalone's FEATURE_CATALOG byte-for-byte. The
  // cross-package equality test went away when this console was extracted, so
  // this parity is hand-maintained.
  HomeNextBestAction: { kind: "feature", label: "Next Best Action" },
  // The remaining six Home-tab sub-cards (ACT-1303), kept CONTIGUOUS with the
  // Home/HomeDemographics/HomeNextBestAction group above rather than scattered
  // through the catalog, so MODULE_ORDER (= Object.keys(FEATURE_CATALOG)) lists
  // the whole Home* family together in the admin console's module matrix. Same
  // shape as HomeDemographics/HomeNextBestAction above: `feature`, not `tab` —
  // each is a sub-card WITHIN the Home tab, not a tab of its own. Order mirrors
  // member-360-standalone's own HOME_CARD_TYPES default card order. Labels MUST
  // match member-360-standalone's FEATURE_CATALOG byte-for-byte (hand-maintained
  // — see the HomeNextBestAction comment above).
  HomeClinicalSummary: { kind: "feature", label: "Clinical Summary" },
  HomeInpatientEd: { kind: "feature", label: "Inpatient & ED" },
  HomeAnnualWellness: { kind: "feature", label: "Annual Wellness" },
  HomeQualityGaps: { kind: "feature", label: "Quality Gaps" },
  HomeHcc: { kind: "feature", label: "HCC Recapture" },
  // T4 — must match member-360-standalone's FEATURE_CATALOG byte-for-byte.
  HomeCases: { kind: "feature", label: "Care Management" },
  // Care Team was a TAB until member-360-standalone #397; it is now a
  // sub-card WITHIN the Home tab, so `feature` like its siblings above.
  // The standalone `CareTeam` tab entry further down is retained but no
  // longer gates a tab in member-360 — see the note there.
  //
  // Its collapsed/expanded default is NOT modeled here: it lives in the
  // module's per-persona `actions[]` as an "expandedByDefault" sentinel.
  // Note the polarity is INVERTED versus HomeDemographics's
  // "collapsedByDefault" — the product directive is an auto-collapsed row,
  // so no sentinel means COLLAPSED.
  //
  // Label MUST match member-360-standalone's FEATURE_CATALOG byte-for-byte
  // (hand-maintained — see the HomeNextBestAction comment above).
  HomeCareTeam: { kind: "feature", label: "Care Team" },
  // CM-8310 / CM-8319 — the member's completed next-best-action history, read from
  // the NBA engine's nba_completion store. A real TAB (unlike the card above): an
  // unbounded chronological feed, not one recommendation. Read-only by design, so
  // again no `actions` — every write goes through the engine's assignNba.
  Activity: { kind: "tab", label: "Activity", tabName: "Activity" },
  Eligibility: { kind: "tab", label: "Eligibility", tabName: "Eligibility" },
  Claims: {
    kind: "tab",
    label: "Claims",
    tabName: "Claims",
    // "printEob" gates the per-claim "Download EOB" CTA in member-360's claim
    // detail view (`useActionAllowed("Claims", "printEob")`). It must be listed
    // here or it never renders as a togglable chip in ActionToggles — an admin
    // would have to hand-type it into the Edit-module modal's allowCustom field.
    // Mirrors member-360-standalone's own FEATURE_CATALOG Claims entry.
    actions: ["view", "submit", "printEob"],
  },
  // RETIRED AS A TAB (member-360-standalone #397) — Care Team renders as the
  // HomeCareTeam sub-card now, and no member-360 ModuleGroup carries the
  // "Care Team" tabName any more, so ordering THIS id no longer moves anything.
  // Kept because live `member_360.persona_modules` rows still reference it and
  // removing it would strand them. Retiring it properly needs its own migration.
  CareTeam: { kind: "tab", label: "Care Team", tabName: "Care Team" },
  Auths: {
    kind: "tab",
    label: "Auths",
    tabName: "Auths",
    actions: ["view", "create"],
  },
  Medications: {
    kind: "tab",
    label: "Medications",
    tabName: "Medications",
    actions: ["view", "edit"],
  },
  Documents: { kind: "tab", label: "Documents", tabName: DOCUMENTS_TAB },
  // G1 (2026-08-19/20) — the chart's visit-note tab (member-360 PR #370). A real
  // TAB, matched by tabName like Documents/Activity above — NOT a `feature`
  // like the Home* cards. Deliberately no `actions`: seeing the tab is the
  // whole grant, mirroring member-360-standalone's own Notes entry byte-for-
  // byte. NOT YET GRANTED to any persona in QA — see sql/0024_notes_module_grant.sql;
  // until that runs, the Notes tab is hidden for EVERY persona, and this id
  // will not appear in the Module dropdown (its options are DB-derived).
  Notes: { kind: "tab", label: "Notes", tabName: "Notes" },
  // AC-ADM-3 (2026-08-25) — Diagnoses / Labs / Scribe. These three rendered in
  // member-360 without ever passing the persona gate: Diagnoses and Labs via the
  // client-side `UNGATED_MODULES` allowlist in roles.ts, Scribe by being appended
  // after the visibility filter. Because the Module dropdown's options are
  // DB-derived (ids already present in `member_360.persona_modules`), and because
  // no persona held a row for any of them, they were also ungrantable and
  // unorderable from this console. sql/0027_tab_module_grants.sql seeds the rows;
  // these entries supply the LABELS, without which the console falls back to the
  // raw id (camelCase) and MODULE_ORDER = Object.keys(FEATURE_CATALOG) sorts them
  // separately. Must stay byte-identical to member-360-standalone's catalog.
  Diagnoses: { kind: "tab", label: "Diagnoses", tabName: "Diagnoses" },
  Labs: { kind: "tab", label: "Labs", tabName: "Labs" },
  Scribe: { kind: "tab", label: "Scribe", tabName: "Scribe" },
  MemberSearch: {
    kind: "route",
    label: "Member Search",
    route: "/members",
    actions: ["search"],
  },
  // Registered for the future — no current FE surface.
  AmbientAI: { kind: "feature", label: "Ambient AI" },
  WriteBack: { kind: "feature", label: "Write Back" },
  Outreach: { kind: "feature", label: "Outreach" },
  NetworkSearch: { kind: "feature", label: "Network Search" },
  PracticeSchedule: { kind: "route", label: "Practice Schedule", route: "/schedule" },
  // P4 host wiring — the ADT Hospital/ED notification bell, app-header CHROME
  // (not a route, not a MemberProfile tab), hence `feature`. Gated at
  // useAdtBellVisible.ts:46. Same class of bug as Notes: gated in the app but
  // granted to nobody until sql/0024_notes_module_grant.sql seeds it (PROVIDER
  // only). Label mirrors member-360-standalone's own FEATURE_CATALOG entry
  // byte-for-byte.
  AdtNotifications: { kind: "feature", label: "ADT Notifications" },
  // P6 host wiring (patient-priority-index) — a full top-level page, mirroring
  // MemberSearch's `route` kind (not a MemberProfile tab). Gated at
  // routeGates.tsx:73 and useTopNavWorklistVisible.ts. Same class of bug as
  // Notes/AdtNotifications: gated in the app but granted to nobody until
  // sql/0024_notes_module_grant.sql seeds it (PROVIDER only). Label + route
  // mirror member-360-standalone's own FEATURE_CATALOG entry byte-for-byte.
  PriorityWorklist: { kind: "route", label: "Priority Worklist", route: "/worklist" },
};

// ---------------------------------------------------------------------------
// Guards / lookups
// ---------------------------------------------------------------------------
/**
 * Runtime guard for any externally-supplied module id (backend payloads, URL
 * params, etc.). The `roles.ts` seed types its values as plain `string[]`, and
 * `connectCurrentUser.currentPersona.modules[].moduleId` comes from a separate
 * service — neither carries a compile-time `ModuleId` guarantee. Validate before
 * trusting them. An unknown/mis-cased id (e.g. "documents") is rejected rather
 * than blindly cast to a value that never matches the catalog (which would hide
 * every gated surface for that persona). Module ids are case-sensitive.
 */
export function isModuleId(value: string): value is ModuleId {
  return Object.prototype.hasOwnProperty.call(FEATURE_CATALOG, value);
}

/**
 * Reverse index `tabName → ModuleId`, built ONCE at module load from the catalog
 * (instead of an O(n) scan on every `moduleIdForTabName` call). The catalog is a
 * static constant, so this map never needs rebuilding.
 */
const TAB_NAME_TO_MODULE_ID: ReadonlyMap<string, ModuleId> = new Map(
  (Object.keys(FEATURE_CATALOG) as ModuleId[]).flatMap((id) => {
    const entry = FEATURE_CATALOG[id];
    return entry.kind === "tab" && entry.tabName !== undefined
      ? [[entry.tabName, id] as const]
      : [];
  }),
);

/**
 * Reverse lookup: given a MemberProfile tab's `tabName`, return the ModuleId
 * that controls it, or `undefined` if no module gates that tab. O(1) via the
 * prebuilt reverse index.
 *
 * As of AC-ADM-3 (2026-08-25) every MemberProfile tab resolves here — Activity,
 * Notes, Documents and finally Diagnoses / Labs / Scribe. `undefined` survives
 * only as the contract for an unrecognised tabName.
 */
export function moduleIdForTabName(tabName: string): ModuleId | undefined {
  return TAB_NAME_TO_MODULE_ID.get(tabName);
}

/**
 * Returns the registered action keys for `moduleId` from the catalog — the
 * vocabulary of what the backend MAY enable for that module. Returns an empty
 * tuple when the module has no registered actions (no actions are expected for
 * that module). The access layer uses this as the allow-all fallback when no
 * raw persona payload is present (offline / dev override / backend absent).
 */
export function catalogActions(moduleId: ModuleId): readonly string[] {
  return FEATURE_CATALOG[moduleId].actions ?? [];
}

// ---------------------------------------------------------------------------
// Single-source-of-truth guard (dev-only runtime check)
// ---------------------------------------------------------------------------
/**
 * Keeps the module-id vocabulary anchored to `roles.ts`. The PRIMARY guard is
 * now compile-time: `roles.ts` PERSONA_MODULES is typed `Record<string,
 * ModuleId[]>`, so any seed id outside the `ModuleId` union fails `tsc`. This
 * DEV-only runtime check is a redundant safety net (and catches the inverse — an
 * id present in the seed but somehow missing a FEATURE_CATALOG entry): at module
 * load in dev it warns if any seed id lacks a catalog entry. No-op in production.
 */
if (import.meta.env.DEV) {
  const missing = Object.values(PERSONA_MODULES)
    .flat()
    .filter((id) => !isModuleId(id));
  if (missing.length) {
    console.warn(
      `[featureCatalog] roles.ts PERSONA_MODULES references module id(s) with no FEATURE_CATALOG entry: ${[
        ...new Set(missing),
      ].join(", ")}. Add a catalog entry so persona/module identity stays in sync.`,
    );
  }
}
